import {
  extractCopyableBlocks,
  recoverCopyableBlocksInSegments,
} from '@littlebird/markdown-utils';
import { memo, useMemo } from 'react';

import { cn } from '@/lib/utils';
import { logChatRenderDebug } from '@/utils/chat-render-debug';

import { CopyableBlock } from './CopyableBlock';
import { MemoizedMarkdownBlock } from './MemoizedMarkdownBlock';
import { SmoothdownRenderContext } from './smoothdown-render-context';
import { parseMarkdownIntoBlocks, stripHiddenTags } from './utils';

type ContentSegment = ReturnType<typeof extractCopyableBlocks>[number];

type MarkdownRenderSegment =
  | {
      type: 'markdown';
      blocks: ReturnType<typeof parseMarkdownIntoBlocks>;
    }
  | {
      type: 'copyable';
      content: string;
      label: string | undefined;
      editable: boolean;
      complete: boolean;
      segmentIndex: number;
    };

function buildRenderSegments(
  contentSegments: ContentSegment[]
): MarkdownRenderSegment[] {
  return contentSegments.map((segment, segmentIndex) => {
    if (segment.type === 'markdown') {
      return {
        type: 'markdown',
        blocks: parseMarkdownIntoBlocks(segment.content),
      };
    }
    return {
      type: 'copyable',
      content: segment.content,
      label: segment.label,
      editable: segment.editable,
      complete: segment.complete,
      segmentIndex,
    };
  });
}

/**
 * Props for the Markdown component.
 */
interface MarkdownProps {
  /** Unique identifier used for keying markdown blocks during rendering */
  id: string;
  /** Raw markdown content to parse and render */
  content: string;
  /** Additional CSS classes to apply to the markdown container */
  className?: string;
  /** Text direction for bidirectional language support */
  dir?: 'ltr' | 'rtl';
  /** Enables parsing <copyable> blocks into the custom copyable UI */
  enableCopyableBlocks?: boolean;
  /** Whether parsed copyable blocks are currently editable */
  copyableBlocksEditable?: boolean;
  /** Called when the user finishes editing a copyable block */
  onCopyableEdit?: (segmentIndex: number, newText: string) => void;
  /** Called when the user asks Littlebird about selected text inside a copyable block editor */
  onAskSelection?: (selection: { text: string }) => void;
  /** Whether the markdown is being rendered in compact Hummingbird chat */
  isCompact?: boolean;
  /** Whether Smoothdown is actively animating this markdown tree */
  isAnimating?: boolean;
  softBreaks?: boolean;
  /** Chat this content belongs to; without it, inline entities like action cards don't render */
  chatId?: string;
  /** How links to action cards draw; the user's own messages draw them as chips */
  actionCardLinks?: 'card' | 'chip';
}

/**
 * Renders markdown content as parsed blocks with support for styling and text direction.
 * Parses the input markdown into blocks and memoizes the result for performance.
 *
 * @param props - The markdown component props
 * @returns A styled markdown container with rendered blocks
 */
export const Markdown = memo(function Markdown({
  content,
  className,
  dir = 'ltr',
  id,
  enableCopyableBlocks = false,
  copyableBlocksEditable = true,
  onCopyableEdit,
  onAskSelection,
  isCompact = false,
  isAnimating = false,
  softBreaks = false,
  chatId,
  actionCardLinks = 'card',
}: MarkdownProps) {
  logChatRenderDebug(
    'markdown-render',
    {
      className,
      contentHash: `${content.length}:${content.slice(0, 24)}:${content.slice(-24)}`,
      contentLength: content.length,
      enableCopyableBlocks,
      id,
      isAnimating,
      preview: content.slice(0, 120),
      softBreaks,
    },
    id
  );

  const renderContextValue = useMemo(
    () => ({ isAnimating, chatId: chatId ?? null, actionCardLinks }),
    [isAnimating, chatId, actionCardLinks]
  );

  const segments = useMemo<MarkdownRenderSegment[]>(() => {
    const cleaned = stripHiddenTags(content);

    if (!enableCopyableBlocks) {
      return [
        {
          type: 'markdown',
          blocks: parseMarkdownIntoBlocks(cleaned),
        },
      ];
    }

    const parsedSegments = extractCopyableBlocks(cleaned);

    const hasLeakedMarkup = parsedSegments.some(
      (segment) =>
        segment.type === 'markdown' && segment.content.includes('<copyable')
    );
    if (!hasLeakedMarkup) {
      return buildRenderSegments(parsedSegments);
    }

    const { segments: recoveredSegments, recoveredCopyableCount } =
      recoverCopyableBlocksInSegments(parsedSegments);
    return buildRenderSegments(
      recoveredCopyableCount > 0 ? recoveredSegments : parsedSegments
    );
  }, [content, enableCopyableBlocks]);

  logChatRenderDebug(
    'markdown-segments',
    {
      id,
      segmentCount: segments.length,
      segments: segments.map((segment) =>
        segment.type === 'markdown'
          ? {
              blockCount: segment.blocks.length,
              firstBlocks: segment.blocks.slice(0, 5).map((block) => ({
                rawHash: `${block.raw.length}:${block.raw.slice(0, 20)}`,
                type: block.type,
              })),
              type: segment.type,
            }
          : {
              complete: segment.complete,
              contentHash: `${segment.content.length}:${segment.content.slice(0, 20)}`,
              editable: segment.editable,
              label: segment.label,
              segmentIndex: segment.segmentIndex,
              type: segment.type,
            }
      ),
    },
    id
  );

  return (
    <div className="smoothdown no-record" dir={dir}>
      <div
        className={cn(
          'markdown',
          'prose prose-bird prose-base select-text cursor-text',
          className
        )}
      >
        <SmoothdownRenderContext.Provider value={renderContextValue}>
          {segments.flatMap((segment, segIndex) => {
            if (segment.type === 'markdown') {
              return segment.blocks.map((block, blockIndex) => (
                <MemoizedMarkdownBlock
                  content={block.raw}
                  type={block.type}
                  softBreaks={softBreaks}
                  key={`${id}-s${segIndex}-b${blockIndex}`}
                />
              ));
            }
            return (
              <CopyableBlock
                key={`${id}-copyable-s${segment.segmentIndex}`}
                content={segment.content}
                label={segment.label}
                editable={segment.editable && copyableBlocksEditable}
                complete={segment.complete}
                isCompact={isCompact}
                onEdit={
                  segment.editable && copyableBlocksEditable && onCopyableEdit
                    ? (newText) => onCopyableEdit(segment.segmentIndex, newText)
                    : undefined
                }
                onAskSelection={onAskSelection}
              />
            );
          })}
        </SmoothdownRenderContext.Provider>
      </div>
    </div>
  );
});
